import {Accordion, AccordionDetails, AccordionSummary, Grid, TextField, Typography} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ExpandMore } from "@mui/icons-material";
import { useRecoilState } from "recoil";
import { useParams } from "react-router-dom";
import React, { useCallback, useContext, useMemo } from "react";
import { ClockPickerView } from "@mui/x-date-pickers";
import { useFormContext, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import { FormAutocomplete, FormDateTimePicker } from "../../../../components/fields";
import { accordionExpandedState } from "../../recoil";
import { IFormValues } from "../../types";
import { RentalLocationType, RouteParams } from "../../../../types";
import { useBookingSubstitutionQueryObserver } from "../../../../api/services";
import DisableFormTextField from "../../_components/DisableFormTextField";
import DisableFormAutocomplete from "../../_components/DisableFormAutocomplete";
import { setDecimalSeparator, shouldDisableRentalDate, shouldDisableRentalTime } from "../../../../utils";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";

const ReplacementCar = () => {
    const { t, i18n } = useTranslation('booking');
    const { id } = useParams<RouteParams>();
    const formContext = useFormContext<IFormValues>();
    const { globalData } = useContext(GlobalContext);

    const substitutionQuery = useBookingSubstitutionQueryObserver(id);
    const [replacementCarExpanded, setReplacementCarExpanded] = useRecoilState(accordionExpandedState('replacementCar'));

    const today = useMemo(() => dayjs().hour(0).minute(0), [t]);
    const dropoffDateRa = useWatch<IFormValues, "originRaData.dropoffDate">({
        name: 'originRaData.dropoffDate'
    });
    const dmsAssociatedOperator = globalData.user.mobilityStores.map(
        ((elem:any) => {
            return elem.sapId;
        })
    );
    // Disabled pickupDate
    const shouldDisablePickupDate = useCallback((date: dayjs.Dayjs) => {
        return shouldDisableRentalDate(date, formContext.getValues('replacementCar.pickupLocation'), RentalLocationType.Pickup, dmsAssociatedOperator);
    }, []);
    // Disabled pickupTime
    const shouldDisablePickupTime = useCallback((time: number, clockTime: ClockPickerView) => {
        return shouldDisableRentalTime(time, clockTime, RentalLocationType.Pickup, formContext.getValues('replacementCar.pickupLocation') , formContext.getValues('replacementCar.pickupDate'), dmsAssociatedOperator);
    }, []);

    const excDamageComponent = useMemo(() => {
        if(!substitutionQuery?.data?.raData) {
            return;
        }
        let excDamage = setDecimalSeparator(((substitutionQuery.data.raData.excDamage ?? 0) / 100).toFixed(2), i18n.language);
        return (
            <TextField
                value={excDamage + substitutionQuery.data.raData.currency}
                label={t('label.excDamage')}
                fullWidth
                InputProps={{readOnly: true}}
                InputLabelProps={{shrink: true}}
                disabled={true}
            />
        )
    }, [substitutionQuery?.data?.raData, t]);

    const excFireTheftComponent = useMemo(() => {
        if(!substitutionQuery?.data?.raData) {
            return;
        }
        let excFireTheft = setDecimalSeparator(((substitutionQuery.data.raData.excFireTheft ?? 0) / 100).toFixed(2), i18n.language);
        return (
            <TextField
                value={excFireTheft + substitutionQuery.data.raData.currency}
                label={t('label.excFireTheft')}
                fullWidth
                InputProps={{readOnly: true}}
                InputLabelProps={{shrink: true}}
                disabled={true}
            />
        )
    }, [substitutionQuery?.data?.raData, t]);

    const ekmComponent = useMemo(() => {
        if(!substitutionQuery?.data?.raData) {
            return;
        }
        let ekm = setDecimalSeparator(((substitutionQuery.data.raData.ekm ?? 0) / 100).toFixed(2), i18n.language);
        return (
            <TextField
                value={ekm + substitutionQuery.data.raData.currency}
                label={t('label.extraKmAmount')}
                fullWidth
                InputProps={{readOnly: true}}
                InputLabelProps={{shrink: true}}
                disabled={true}
            />
        )
    }, [substitutionQuery?.data?.raData, t]);

    return(
        <Accordion expanded={replacementCarExpanded} onChange={(e, expanded) => setReplacementCarExpanded(expanded)}>
            <AccordionSummary expandIcon={<ExpandMore />}
                              sx={{
                                  '.MuiAccordionSummary-content': {
                                      justifyContent: "space-between",
                                      alignItems: "center"
                                  }
                              }}
            >
                <Typography variant="h5">
                    {t('label.replacementCar_title')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid item container rowSpacing={1} columnSpacing={3}>
                    <Grid item xs={12} sm={3}>
                        <FormAutocomplete<IFormValues>
                            name="replacementCar.pickupLocation"
                            label={(t('label.pickupLocation') + '*')}
                            options={substitutionQuery.data?.locations ?? []}
                            loading={substitutionQuery.isLoading}
                            isOptionEqualToValue={(option, value) => option.id === value.id} /* todo verificare*/
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormDateTimePicker<IFormValues>
                            name="replacementCar.pickupDate"
                            label={(t('label.pickupDate') + '*')}
                            maxDateTime={dropoffDateRa}
                            minDateTime={!!dropoffDateRa && today.isBefore(dropoffDateRa?.subtract(30, 'day')) ? dropoffDateRa.subtract(30, 'day') : today}
                            shouldDisableDate={shouldDisablePickupDate}
                            shouldDisableTime={shouldDisablePickupTime}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <DisableFormTextField name="replacementCar.dropoffLocation" label={'label.dropoffLocation'} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormDateTimePicker<IFormValues>
                            name="replacementCar.dropoffDate"
                            label={(t('label.dropoffDate'))}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <DisableFormTextField name="replacementCar.vehicleType" label={'label.bookedVehicleType'}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <DisableFormTextField name="replacementCar.carGroup" label={'label.carGroup'}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <DisableFormAutocomplete name="replacementCar.ancillaries" label={'label.ancillaries'}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        {excDamageComponent}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        {excFireTheftComponent}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <DisableFormTextField name="replacementCar.mileageIncluded" label={'label.mileageIncluded'}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        {ekmComponent}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}

export default  ReplacementCar;
